import {
  Modal, ModalBody, ModalContent, ModalHeader,
} from '@heroui/react';
import { toPng } from 'html-to-image';
import { jsPDF } from 'jspdf';
import {
  Copy, Download, FileText, Settings as SettingsIcon,
} from 'lucide-react';
import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'react-qr-code';
import styled from 'styled-components';

import { useHeroToast } from '@/hooks';
import { media } from '@/utils';

interface WebAppQRSectionProps {
  url: string;
  isLoading?: boolean;
  error?: Error | null;
}

/* ============================================================
   Kompaktowy blok QR aplikacji webowej.
   Layout: QR (140×140) po lewej + URL & akcje po prawej.
   Na mobile zwija się do kolumny.
   ============================================================ */

const Card = styled.div`
  background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.background.card : '#ffffff')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 12px;

  ${media.tabletDown} {
    flex-wrap: wrap;
    padding: 10px 12px;
    gap: 10px;
  }
`;

const QrFrame = styled.button`
  width: 44px;
  height: 44px;
  padding: 4px;
  border-radius: 6px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: zoom-in;
  transition: border-color 0.15s ease, transform 0.15s ease;

  &:hover {
    border-color: #1a4d4d;
    transform: scale(1.04);
  }
`;

/* Modal — duży podgląd QR po kliknięciu w thumbnail. */
const ModalQrFrame = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 8px 0 16px;
`;

const ModalQrInner = styled.div`
  width: 280px;
  height: 280px;
  padding: 16px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalUrlText = styled.code`
  font-size: 12.5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f9fafb')};
  border-radius: 6px;
  padding: 6px 10px;
  word-break: break-all;
  text-align: center;
  line-height: 1.5;
  max-width: 320px;
`;

const UrlValue = styled.code`
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  color: ${({ theme }) => theme.colors.text.primary};
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f9fafb')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 6px;
  padding: 7px 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;

  ${media.tabletDown} {
    flex-wrap: wrap;
    width: 100%;
  }
`;

const CompactButton = styled.button<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 400;
  min-height: 30px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;
  background: ${({ $primary, theme }) => {
    if ($primary) return '#111827';
    return theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#ffffff';
  }};
  color: ${({ $primary, theme }) => {
    if ($primary) return '#ffffff';
    return theme.colors.text.primary;
  }};
  border: 1px solid ${({ $primary, theme }) => {
    if ($primary) return 'transparent';
    return theme.colors.border.primary;
  }};
  box-shadow: ${({ $primary }) => ($primary ? '0 1px 2px rgba(0, 0, 0, 0.18)' : 'none')};

  svg { width: 13px; height: 13px; }

  &:hover {
    ${({ $primary, theme }) => ($primary
    ? `
      background: #000000;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28);
    `
    : `
      background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f9fafb'};
      border-color: ${theme.mode === 'dark' ? '#4b5563' : '#d1d5db'};
    `)}
  }

  &:active {
    transform: scale(0.97);
  }

  ${({ theme, $primary }) => ($primary && theme.mode === 'dark'
    ? `
    background: #1f2937;
    border-color: #374151;

    &:hover {
      background: #111827;
    }
  `
    : '')}
`;

const EmptyState = styled.div`
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff')};
  border: 1px dashed ${({ theme }) => theme.colors.border.primary};
  border-radius: 10px;
  padding: 22px 18px;
  display: flex;
  align-items: center;
  gap: 14px;

  svg {
    width: 22px;
    height: 22px;
    color: ${({ theme }) => theme.colors.text.secondary};
    flex-shrink: 0;
  }
`;

const EmptyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const EmptyTitle = styled.span`
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyDescription = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.45;
`;

const Skeleton = styled.div`
  height: 156px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.04) 0%,
    rgba(0, 0, 0, 0.07) 50%,
    rgba(0, 0, 0, 0.04) 100%
  );
  background-size: 200% 100%;
  animation: webShimmer 1.6s ease-in-out infinite;

  @keyframes webShimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

export const WebAppQRSection: FC<WebAppQRSectionProps> = ({ url, isLoading, error }) => {
  const { t } = useTranslation();
  // qrRef wskazuje na zawsze-renderowany off-screen 280×280 QR — dzięki temu
  // export do PDF/PNG ma wysoką rozdzielczość niezależnie od tego czy
  // modal podglądu jest aktualnie otwarty.
  const qrRef = useRef<HTMLDivElement>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { showSuccess, showError } = useHeroToast();

  const handleDownloadPDF = async (): Promise<void> => {
    if (qrRef.current) {
      const dataUrl = await toPng(qrRef.current, { backgroundColor: 'white' });
      // eslint-disable-next-line new-cap
      const pdf = new jsPDF();
      const imgProps = pdf.getImageProperties(dataUrl);
      const pdfWidth = 100;
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(dataUrl, 'PNG', 10, 10, pdfWidth, pdfHeight);
      pdf.save('hotelspot-qr-code.pdf');
    }
  };

  const handleDownloadPNG = async (): Promise<void> => {
    if (qrRef.current) {
      const dataUrl = await toPng(qrRef.current, { backgroundColor: 'white' });
      const link = document.createElement('a');
      link.download = 'hotelspot-web-qr-code.png';
      link.href = dataUrl;
      link.click();
    }
  };

  const handleCopyUrl = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      showSuccess(String(t('webApp.linkCopied', 'Link skopiowany')));
    } catch (_err) {
      showError(String(t('webApp.copyFailed', 'Nie udało się skopiować')));
    }
  };

  if (isLoading) {
    return <Skeleton />;
  }

  if (error) {
    return (
      <EmptyState>
        <SettingsIcon />
        <EmptyText>
          <EmptyTitle>{t('webApp.errorTitle', 'Nie udało się załadować')}</EmptyTitle>
          <EmptyDescription>
            {error?.message || t('webApp.errorDescription', 'Wystąpił problem podczas pobierania konfiguracji aplikacji webowej.')}
          </EmptyDescription>
        </EmptyText>
      </EmptyState>
    );
  }

  if (!url || url.trim() === '') {
    return (
      <EmptyState>
        <SettingsIcon />
        <EmptyText>
          <EmptyTitle>{t('webApp.notConfiguredTitle', 'Aplikacja webowa nieskonfigurowana')}</EmptyTitle>
          <EmptyDescription>
            {t('webApp.notConfiguredDescription', 'Skontaktuj się z opiekunem konta, aby skonfigurować adres aplikacji webowej.')}
          </EmptyDescription>
        </EmptyText>
      </EmptyState>
    );
  }

  return (
    <>
      <Card>
        <QrFrame
          type="button"
          onClick={() => setIsPreviewOpen(true)}
          aria-label={String(t('webApp.viewLargeQr', 'Pokaż większy kod QR'))}
          title={String(t('webApp.viewLargeQr', 'Pokaż większy kod QR'))}
        >
          <QRCode
            size={36}
            style={{ height: 36, width: 36 }}
            bgColor="transparent"
            value={url}
            viewBox="0 0 1024 1024"
          />
        </QrFrame>

        <UrlValue title={url}>{url}</UrlValue>

        <ButtonRow>
          <CompactButton type="button" $primary onClick={handleDownloadPDF}>
            <FileText />
            {t('webApp.downloadPdf', 'PDF')}
          </CompactButton>
          <CompactButton type="button" onClick={handleDownloadPNG}>
            <Download />
            {t('webApp.downloadPng', 'PNG')}
          </CompactButton>
          <CompactButton type="button" onClick={handleCopyUrl}>
            <Copy />
            {t('webApp.copyUrl', 'Kopiuj link')}
          </CompactButton>
        </ButtonRow>
      </Card>

      {/* Hidden 280×280 QR — zawsze renderowany dla wysokiej jakości eksportu PDF/PNG. */}
      <div
        style={{
          position: 'absolute',
          left: '-9999px',
          top: '-9999px',
          pointerEvents: 'none',
        }}
        aria-hidden
      >
        <div
          ref={qrRef}
          style={{
            width: 280,
            height: 280,
            padding: 16,
            background: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <QRCode
            size={248}
            style={{ height: 248, width: 248 }}
            bgColor="transparent"
            value={url}
            viewBox="0 0 1024 1024"
          />
        </div>
      </div>

      <Modal
        isOpen={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        size="md"
        radius="md"
        backdrop="opaque"
        scrollBehavior="inside"
        classNames={{
          header: 'pb-3 px-6 pt-5 border-b border-divider',
          body: 'p-6',
          closeButton: 'top-3 right-3 w-9 h-9 [&_svg]:w-5 [&_svg]:h-5 hover:bg-default-100',
        }}
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>
                <span style={{ fontSize: 16, fontWeight: 300 }}>
                  {t('webApp.qrPreview', 'Kod QR aplikacji webowej')}
                </span>
              </ModalHeader>
              <ModalBody>
                <ModalQrFrame>
                  <ModalQrInner>
                    <QRCode
                      size={248}
                      style={{ height: 248, width: 248 }}
                      bgColor="transparent"
                      value={url}
                      viewBox="0 0 1024 1024"
                    />
                  </ModalQrInner>
                  <ModalUrlText>{url}</ModalUrlText>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <CompactButton type="button" $primary onClick={handleDownloadPDF}>
                      <FileText />
                      {t('webApp.downloadPdf', 'PDF')}
                    </CompactButton>
                    <CompactButton type="button" onClick={handleDownloadPNG}>
                      <Download />
                      {t('webApp.downloadPng', 'PNG')}
                    </CompactButton>
                    <CompactButton type="button" onClick={handleCopyUrl}>
                      <Copy />
                      {t('webApp.copyUrl', 'Kopiuj link')}
                    </CompactButton>
                  </div>
                </ModalQrFrame>
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
